import { settings } from './storage.js';
import { device } from './device.js';

/* Sonidos de navegación: un «tic» suave al mover el foco, un clic al elegir, un tono descendente al volver o cerrar (la X, la
   flecha atrás) y un par de notas al cambiar de canal, de capítulo o al abrir una película. Se sintetizan con Web Audio (sin
   ficheros), muy cortos y bajitos. Ajuste «Sonidos de navegación»: Automático (solo en la tele) | Siempre | No. */

export const MODES = ['auto', 'on', 'off'];

/** Cada sonido: lista de notas { f: Hz inicial, to: Hz final, at: segundo de inicio, d: duración, v: volumen, type }. */
export const SOUNDS = {
    move: [{ f: 1500, to: 1300, at: 0, d: 0.035, v: 0.05, type: 'sine' }],
    select: [{ f: 880, to: 1180, at: 0, d: 0.07, v: 0.11, type: 'triangle' }],
    back: [{ f: 640, to: 380, at: 0, d: 0.11, v: 0.11, type: 'triangle' }],
    channel: [{ f: 660, to: 660, at: 0, d: 0.07, v: 0.1, type: 'sine' }, { f: 990, to: 990, at: 0.07, d: 0.1, v: 0.1, type: 'sine' }],
    open: [{ f: 520, to: 780, at: 0, d: 0.09, v: 0.1, type: 'triangle' }, { f: 780, to: 1040, at: 0.08, d: 0.12, v: 0.1, type: 'triangle' }]
};

export function soundsEnabled({ mode = 'auto', tv = false } = {}) {
    return mode === 'on' || (mode !== 'off' && !!tv);
}

let ctx = null;
const lastAt = {};

function context() {
    if (ctx) return ctx;
    const AC = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!AC) return null;
    try { ctx = new AC(); } catch (e) { ctx = null; }
    return ctx;
}

const tvNow = () => device.isTV || (typeof document !== 'undefined' && document.documentElement.classList.contains('mode-tv'));

export function playSound(name, { force = false } = {}) {
    if (!force && !soundsEnabled({ mode: settings.get('uiSounds') || 'auto', tv: tvNow() })) return;
    const notes = SOUNDS[name];
    if (!notes) return;
    const now = Date.now();
    if (now - (lastAt[name] || 0) < (name === 'move' ? 55 : 90)) return; // evita ráfagas
    lastAt[name] = now;
    const ac = context();
    if (!ac) return;
    try {
        if (ac.state === 'suspended') ac.resume().catch(() => {});
        const t0 = ac.currentTime + 0.005;
        notes.forEach((n) => {
            const osc = ac.createOscillator();
            const gain = ac.createGain();
            osc.type = n.type || 'sine';
            osc.frequency.setValueAtTime(n.f, t0 + n.at);
            if (n.to !== n.f) osc.frequency.exponentialRampToValueAtTime(n.to, t0 + n.at + n.d);
            gain.gain.setValueAtTime(0.0001, t0 + n.at);
            gain.gain.exponentialRampToValueAtTime(n.v, t0 + n.at + 0.008);
            gain.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + n.d);
            osc.connect(gain);
            gain.connect(ac.destination);
            osc.start(t0 + n.at);
            osc.stop(t0 + n.at + n.d + 0.02);
        });
    } catch (e) { /* sin audio: no pasa nada */ }
}

/** ¿Es un botón de cerrar o volver? (la X, la flecha atrás, «Cancelar»…) */
export function isBackControl(el) {
    if (!el || !el.closest) return false;
    if (el.closest('[data-action="close"], [data-action="closeside"], [data-action="back"], .popup-close, .link.back, .sheet-close, [data-back]')) return true;
    const btn = el.closest('button, a, .btn');
    if (!btn) return false;
    const icon = btn.querySelector ? btn.querySelector('.mi') : null;
    const name = icon ? String(icon.textContent || '').trim() : '';
    return name === 'close' || name === 'arrow_back' || name === 'arrow_back_ios';
}

/** ¿Es algo que se «elige» (botón, enlace, tarjeta, fila)? */
export function isChoice(el) {
    return !!(el && el.closest && el.closest('button, a[href], .btn, .card, [data-open], [data-play-live], [data-play-episode], .chitem, .mitem, .chip, .setting, .ps-tile, .episode, .seg-btn, .filter-btn, [role="button"]'));
}

export function initUiSounds({ player } = {}) {
    if (typeof document === 'undefined') return;
    // Mover el foco (mando de la tele): el aviso lo emite tvnav al enfocar un elemento.
    document.addEventListener('nav:focus', () => playSound('move'), true);
    // Elegir y cerrar: al pulsar o hacer clic.
    document.addEventListener('click', (e) => {
        if (isBackControl(e.target)) playSound('back');
        // Los que arrancan un canal, un capítulo o una película ya suenan con su propio aviso (abrir / cambiar de canal).
        else if (isChoice(e.target) && !(e.target.closest && e.target.closest('[data-play-live], [data-play-episode], [data-action="side-channel"], [data-action="side-episode"], .ps-tile'))) playSound('select');
    }, true);
    // Tecla de volver (mando, teclado).
    document.addEventListener('keydown', (e) => {
        if (e.repeat || e.defaultPrevented) return;
        if (e.key === 'Escape' || e.key === 'BrowserBack' || e.key === 'GoBack' || e.keyCode === 4 || e.keyCode === 10009) playSound('back');
    }, true);
    if (player && typeof player.on === 'function') {
        player.on('open', () => playSound('open'));
        player.on('change', () => playSound('channel'));
        player.on('close', () => playSound('back'));
    }
}
